import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { AdvancedMarker, Map, Pin } from "@vis.gl/react-google-maps";
import logo from "../../assets/IconMetuaa.png";
import GoogleMapLoader from "../../Googlemaploader";
import { apiUrl } from "../../Constants";
import "./HomePage.css";

const headerLinks = [
  { label: "Covoiturage", href: "/" },
  { label: "Bus", href: "/" },
  { label: "Train", href: "/" },
  { label: "Livraison", href: "/" },
  { label: "Courses", href: "/" },
  { label: "Offres", href: "/", badge: "Nouveau" },
];

const footerColumns = [
  {
    title: "Entreprise",
    links: ["À propos", "Carrières", "Presse", "Blog"],
  },
  {
    title: "Services",
    links: ["Covoiturage", "Bus", "Train", "Livraison", "Courses urbaines"],
  },
  {
    title: "Support",
    links: ["Centre d'aide", "Contact", "Conditions d'utilisation", "Politique de confidentialité"],
  },
];

const summaryTabs = [
  { label: "Tous", count: 72, active: true, mode: "all", subtitle: "Meilleures options" },
  { label: "Covoiturage", count: 45, active: false, mode: "car" },
  { label: "Bus", count: 21, active: false, mode: "bus" },
  { label: "Train", count: 6, active: false, mode: "train" },
];

const sortOptions = [
  { label: "Départ le plus tôt", checked: true, icon: "clock" },
  { label: "Prix le plus bas", icon: "price" },
  { label: "Durée du trajet", icon: "clock" },
  { label: "Meilleures évaluations", icon: "star" },
  { label: "Trajets recommandés", icon: "thumb" },
];

const departureSlots = [
  { label: "00h00 - 06h00", count: 12 },
  { label: "06h00 - 12h00", count: 28 },
  { label: "12h00 - 18h00", count: 34, checked: true },
  { label: "18h00 - 23h59", count: 18 },
];

const comfortOptions = [
  { label: "Climatisation", count: 67 },
  { label: "Wi-Fi", count: 54 },
  { label: "Prises USB", count: 49 },
];

const onboardOptions = [
  { label: "Toilettes", count: 41 },
  { label: "Collation offerte", count: 36 },
];

const vehicleOptions = [{ label: "Berline", count: 24 }];

const DEFAULT_SEARCH_PARAMS = {
  from: "Yaoundé",
  to: "Douala",
  date: formatDateForApi(new Date()),
  passengers: 1,
};

const resultCards = [
  {
    time: "14:30",
    duration: "3h00",
    arrival: "17:30",
    from: "Yaoundé",
    fromSub: "Mvan",
    to: "Douala",
    toSub: "Gare Routière Bonabéri",
    mode: "Covoiturage",
    company: "Jean M.",
    avatar: "JM",
    rating: "4.8",
    seats: "4 places disponibles",
    price: "3 500",
    currency: "FCFA",
    tag: "Meilleur prix",
    tagTone: "success",
    extras: ["Car", "Seat", "WiFi"],
  },
  {
    time: "15:00",
    duration: "3h30",
    arrival: "18:30",
    from: "Yaoundé",
    fromSub: "Mvan",
    to: "Douala",
    toSub: "Gare Routière Bonabéri",
    mode: "Bus",
    company: "Express Voyages",
    avatar: "EV",
    rating: "",
    seats: "Toilettes à bord",
    price: "5 000",
    currency: "FCFA",
    tag: "Très demandé",
    tagTone: "warning",
    extras: ["Bus", "Toilet", "Air"],
  },
  {
    time: "16:00",
    duration: "3h15",
    arrival: "19:15",
    from: "Yaoundé",
    fromSub: "Mvan",
    to: "Douala",
    toSub: "Gare Routière Bonabéri",
    mode: "Covoiturage",
    company: "Fabrice K.",
    avatar: "FK",
    rating: "4.9",
    seats: "3 places disponibles",
    price: "4 000",
    currency: "FCFA",
    tag: "",
    tagTone: "neutral",
    extras: ["Car", "Seat"],
  },
  {
    time: "17:00",
    duration: "3h20",
    arrival: "20:20",
    from: "Yaoundé",
    fromSub: "Mvan",
    to: "Douala",
    toSub: "Gare Routière Bonabéri",
    mode: "Train",
    company: "Camrail",
    avatar: "CR",
    rating: "",
    seats: "Wi-Fi",
    price: "7 500",
    currency: "FCFA",
    tag: "",
    tagTone: "neutral",
    extras: ["Train", "WiFi", "Air"],
  },
  {
    time: "18:00",
    duration: "3h40",
    arrival: "21:40",
    from: "Yaoundé",
    fromSub: "Mvan",
    to: "Douala",
    toSub: "Gare Routière Bonabéri",
    mode: "Bus",
    company: "General Express",
    avatar: "GE",
    rating: "",
    seats: "Climatisé",
    price: "5 500",
    currency: "FCFA",
    tag: "Très demandé",
    tagTone: "warning",
    extras: ["Bus", "Toilet", "Air"],
  },
];

function formatDateForApi(date) {
  if (!date) return "";
  if (typeof date === "string") return date.slice(0, 10);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateLabel(date) {
  if (!date) return "Aujourd'hui, 24 mai";
  const [year, month, day] = String(date).split("-").map(Number);
  if (!year || !month || !day) return "Aujourd'hui, 24 mai";
  return new Date(year, month - 1, day).toLocaleDateString("fr-FR", { day: "numeric", month: "long" });
}

function normalizeSearchResult(result) {
  const departure = new Date(result?.departure_at);
  const arrival = new Date(result?.estimated_arrival_at);
  const durationMinutes = Number.isNaN(departure.getTime()) || Number.isNaN(arrival.getTime())
    ? null
    : Math.max(0, Math.round((arrival.getTime() - departure.getTime()) / 60000));

  const formatTime = (date) => (Number.isNaN(date.getTime())
    ? "--"
    : date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", hour12: false }));

  const price = Number(result?.price_per_seat ?? 0);
  const driverName = [result?.driver?.first_name, result?.driver?.last_name].filter(Boolean).join(" ").trim();

  return {
    id: result?.id || `${result?.type}-${result?.departure_at}-${result?.price_per_seat}`,
    time: formatTime(departure),
    duration: durationMinutes ? `${Math.round(durationMinutes / 60)}h${String(durationMinutes % 60).padStart(2, "0")}` : "--",
    arrival: formatTime(arrival),
    from: result?.origin?.addressName || result?.origin?.name || "Départ",
    fromSub: result?.origin?.name || result?.origin?.addressName || "",
    to: result?.destination?.addressName || result?.destination?.name || "Destination",
    toSub: result?.destination?.name || result?.destination?.addressName || "",
    mode: result?.type === "train" ? "Train" : result?.type === "bus" ? "Bus" : "Covoiturage",
    company: driverName || "Conducteur",
    avatar: driverName ? driverName.split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase() : "TR",
    rating: "",
    seats: `${result?.available_seats ?? 0} places disponibles`,
    price: price.toLocaleString("fr-FR"),
    currency: result?.currency || "FCFA",
    tag: result?.type === "carpool" ? "Disponible" : "",
    tagTone: result?.type === "carpool" ? "success" : "neutral",
    extras: [
      result?.type === "train" ? "Train" : result?.type === "bus" ? "Bus" : "Car",
      "Seat",
    ],
    raw: result,
  };
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="7.5" />
      <path d="m16.5 16.5 4.5 4.5" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="7.5" r="3.5" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

function MenuChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function FooterFacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13.4 20v-7h2.4l.4-2.7h-2.8V8.6c0-.8.2-1.5 1.3-1.5h1.6V4.7c-.3 0-1.1-.1-2.1-.1-2.1 0-3.5 1.2-3.5 3.6v2.1H8.4V13h2.3v7h2.7Z" />
    </svg>
  );
}

function FooterXIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 5h3.1l3.7 5 4-5H19l-5.2 6.5L19.4 19h-3.1l-4.1-5.5L7.8 19H5.6l5.5-6.9L6 5Z" />
    </svg>
  );
}

function FooterYoutubeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 12c0 2.6-.3 4.2-.7 5-.2.5-.6.9-1.1 1.1-.9.4-3.5.7-7.2.7s-6.3-.3-7.2-.7c-.5-.2-.9-.6-1.1-1.1-.4-.8-.7-2.4-.7-5s.3-4.2.7-5c.2-.5.6-.9 1.1-1.1.9-.4 3.5-.7 7.2-.7s6.3.3 7.2.7c.5.2.9.6 1.1 1.1.4.8.7 2.4.7 5Z" />
      <path fill="#fff" d="m10 8.8 5 3.2-5 3.2V8.8Z" />
    </svg>
  );
}

function FooterInstagramIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="5" width="14" height="14" rx="4" />
      <circle cx="12" cy="12" r="3.3" />
      <circle cx="16.6" cy="7.6" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function FooterTiktokIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14.2 4.5c.5 1.6 1.7 2.8 3.3 3.2v2.4a6.3 6.3 0 0 1-3.1-.8v5.1a4.8 4.8 0 1 1-4.8-4.8c.3 0 .7 0 1 .1v2.5a2.5 2.5 0 1 0 1.5 2.2V4.5h2.1Z" />
    </svg>
  );
}

function LocationIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 21s6-5.3 6-11a6 6 0 0 0-12 0c0 5.7 6 11 6 11Z" />
      <circle cx="12" cy="10" r="2.25" fill="none" />
    </svg>
  );
}

function SwapIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m6 8 3-3 3 3" />
      <path d="M9 5v14" />
      <path d="m18 16-3 3-3-3" />
      <path d="M15 19V5" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
      <path d="M8 2.8v4M16 2.8v4M3 9h18" />
    </svg>
  );
}

function PassengerIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="7.5" r="3.5" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m14 6-6 6 6 6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m10 6 6 6-6 6" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

function CarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M5 13.5 6.6 9.2A2.4 2.4 0 0 1 8.9 7.5h6.2a2.4 2.4 0 0 1 2.3 1.7L19 13.5" />
      <rect x="4" y="13.5" width="16" height="4.5" rx="1.5" />
      <circle cx="8" cy="18.5" r="1.2" />
      <circle cx="16" cy="18.5" r="1.2" />
    </svg>
  );
}

function BusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="5" y="4.5" width="14" height="14" rx="3" />
      <path d="M8 8h8M8 11.5h8M8 15h5" />
      <circle cx="8" cy="18.5" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="16" cy="18.5" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function TrainIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="6" y="4.5" width="12" height="14.5" rx="3" />
      <path d="M8.5 8h7M8.5 11.5h7M8.5 15h7" />
      <path d="M9 19 7.8 20.5M15 19 16.2 20.5" />
      <circle cx="10" cy="18.5" r="0.75" fill="currentColor" stroke="none" />
      <circle cx="14" cy="18.5" r="0.75" fill="currentColor" stroke="none" />
    </svg>
  );
}

function SearchFieldIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="7.5" />
      <path d="m17.5 17.5 3.5 3.5" />
    </svg>
  );
}

function SearchButtonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
      <circle cx="11" cy="11" r="7.5" />
      <path d="m16.5 16.5 4.5 4.5" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v5l3 2" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="m12 4 2.6 5.2 5.8.8-4.2 4.1 1 5.8L12 17.7 6.8 20l1-5.8L3.6 10l5.8-.8L12 4Z" />
    </svg>
  );
}

function ThumbIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M9.5 20h8a2 2 0 0 0 2-1.7l1-6A2 2 0 0 0 18.5 10H14l.7-3.5A2 2 0 0 0 12.8 4c-.8 0-1.6.5-1.9 1.3L8.5 10H5v10h4.5Z" />
    </svg>
  );
}

function WifiIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M5 9a11 11 0 0 1 14 0" />
      <path d="M8.5 12a6 6 0 0 1 7 0" />
      <path d="M12 15.5h0" />
    </svg>
  );
}

function ToiletIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M8 4.5h8v3H8z" />
      <path d="M9 7.5v5a3 3 0 0 0 6 0v-5" />
      <path d="M7.5 13h9l-.8 6h-7.4l-.8-6Z" />
    </svg>
  );
}

function SeatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M8 5v8a2 2 0 0 0 2 2h7" />
      <path d="M7 15h10l-1 4H8l-1-4Z" />
    </svg>
  );
}

function AirIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M4 12h16" />
      <path d="M8 8.5V15" />
      <path d="M12 7v10" />
      <path d="M16 8.5V15" />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M8 5.5 3.5 7.5v11l4.5-2 4.5 2 4.5-2 4.5 2v-11L17 5.5l-4.5 2-4.5-2Z" />
      <path d="M8 5.5v11M13 7.5v11M17 5.5v11" />
    </svg>
  );
}

function RadioIcon({ checked }) {
  return <span className={`ride-radio ${checked ? "is-checked" : ""}`} aria-hidden="true" />;
}

function CheckboxIcon({ checked }) {
  return <span className={`ride-checkbox ${checked ? "is-checked" : ""}`} aria-hidden="true" />;
}

function ModeIcon({ mode }) {
  if (mode === "Bus") return <BusIcon />;
  if (mode === "Train") return <TrainIcon />;
  return <CarIcon />;
}

function AmenityIcon({ kind }) {
  if (kind === "Bus") return <BusIcon />;
  if (kind === "Train") return <TrainIcon />;
  if (kind === "Toilet") return <ToiletIcon />;
  if (kind === "WiFi") return <WifiIcon />;
  if (kind === "Air") return <AirIcon />;
  if (kind === "Seat") return <SeatIcon />;
  return <CarIcon />;
}

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setIsMenuOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMenuOpen]);

  return (
    <header className="bbc-nav-shell">
      <div className="bbc-container bbc-nav-container">
        <div className="bbc-nav">
          <Link to="/" className="bbc-logo" aria-label="METUA'A">
            <img src={logo} alt="Logo METUA'A" className="bbc-logo-image" />
            <span>METUA&apos;A</span>
          </Link>

          <nav className="bbc-main-links" aria-label="Navigation principale">
            {headerLinks.map((item) => (
              <a key={item.label} href={item.href} className="bbc-main-link">
                <span>{item.label}</span>
                {item.badge ? <span className="bbc-main-badge">{item.badge}</span> : null}
              </a>
            ))}
          </nav>

          <div className="bbc-right">
            <button type="button" className="bbc-icon-btn" aria-label="Rechercher">
              <SearchIcon />
            </button>
            <a href="/" className="bbc-propose">
              Proposer un trajet
            </a>
            <span className="bbc-flag" aria-label="France">
              <i />
              <i />
              <i />
            </span>
            <button
              type="button"
              className="bbc-avatar-btn"
              aria-label="Ouvrir le menu utilisateur"
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen((current) => !current)}
            >
              <span className="bbc-avatar" aria-hidden="true">
                <UserIcon />
              </span>
            </button>
          </div>

          {isMenuOpen ? (
            <div className="bbc-user-menu" ref={menuRef}>
              <Link to="/" className="bbc-user-menu-item" onClick={() => setIsMenuOpen(false)}>
                <span>Connexion</span>
                <MenuChevronIcon />
              </Link>
              <Link to="/" className="bbc-user-menu-item" onClick={() => setIsMenuOpen(false)}>
                <span>Inscription</span>
                <MenuChevronIcon />
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bbc-footer">
      <div className="bbc-container bbc-footer-inner">
        <div className="bbc-footer-top">
          <div className="bbc-footer-branding">
            <Link to="/" className="bbc-footer-logo" aria-label="METUA'A">
              <img src={logo} alt="Logo METUA'A" className="bbc-footer-logo-image" />
              <span>METUA&apos;A</span>
            </Link>
            <p>
              Metua'a est la super application de mobilité au Cameroun.
              Covoiturage, Bus, Train, Livraison et bien plus encore.
            </p>
            <div className="bbc-social" aria-label="Reseaux sociaux">
              <a href="/" aria-label="Facebook"><FooterFacebookIcon /></a>
              <a href="/" aria-label="X"><FooterXIcon /></a>
              <a href="/" aria-label="YouTube"><FooterYoutubeIcon /></a>
              <a href="/" aria-label="Instagram"><FooterInstagramIcon /></a>
              <a href="/" aria-label="TikTok"><FooterTiktokIcon /></a>
            </div>
          </div>

          <div className="bbc-footer-grid">
            {footerColumns.map((column) => (
              <div key={column.title}>
                <h4>{column.title}</h4>
                <ul>
                  {column.links.map((link) => (
                    <li key={link}>
                      <a href="/">{link}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="bbc-footer-newsletter">
            <h4>Newsletter</h4>
            <p>Recevez nos offres et nouveautés</p>
            <form className="bbc-newsletter-form">
              <input type="email" placeholder="Votre email" aria-label="Votre email" />
              <button type="button">S'abonner</button>
            </form>
          </div>
        </div>

        <div className="bbc-footer-bottom">
          <div>© 2024 Metua'. Tous droits réservés.</div>
          <div className="bbc-footer-made">
            <span>Made with</span>
            <span className="bbc-heart" aria-hidden="true">♥</span>
            <span>in Cameroun</span>
            <span className="bbc-cameroon-flag" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function SearchBar({ searchParams, setSearchParams, onSearch, searchLoading }) {
  const currentDate = searchParams?.date || formatDateForApi(new Date());

  return (
    <section className="ride-search-shell">
      <div className="ride-search-card">
        <div className="ride-search-grid">
          <label className="ride-search-field">
            <span className="ride-search-icon"><LocationIcon /></span>
            <input
              type="text"
              value={searchParams.from || ""}
              onChange={(event) => setSearchParams((current) => ({ ...current, from: event.target.value }))}
              placeholder="Départ"
              aria-label="Départ"
            />
          </label>

          <div className="ride-search-swap">
            <span className="ride-swap-bubble" aria-hidden="true">
              <SwapIcon />
            </span>
          </div>

          <label className="ride-search-field">
            <span className="ride-search-icon"><LocationIcon /></span>
            <input
              type="text"
              value={searchParams.to || ""}
              onChange={(event) => setSearchParams((current) => ({ ...current, to: event.target.value }))}
              placeholder="Arrivée"
              aria-label="Arrivée"
            />
          </label>

          <label className="ride-search-field">
            <span className="ride-search-icon"><CalendarIcon /></span>
            <input
              type="date"
              value={currentDate}
              onChange={(event) => setSearchParams((current) => ({ ...current, date: event.target.value }))}
              aria-label="Date"
            />
          </label>

          <label className="ride-search-field ride-search-passenger">
            <span className="ride-search-icon"><UserIcon /></span>
            <input
              type="number"
              min="1"
              max="8"
              value={searchParams.passengers || 1}
              onChange={(event) => setSearchParams((current) => ({ ...current, passengers: Math.max(1, Number(event.target.value) || 1) }))}
              aria-label="Nombre de passagers"
            />
            <ChevronDownIcon />
          </label>

          <button type="button" className="ride-search-button" onClick={onSearch} disabled={searchLoading}>
            <span>{searchLoading ? "Recherche..." : "Rechercher"}</span>
            <SearchButtonIcon />
          </button>
        </div>
      </div>
    </section>
  );
}

function SidebarFilterGroup({ title, action, children }) {
  return (
    <section className="ride-filter-group">
      <div className="ride-filter-head">
        <h3>{title}</h3>
        {action ? <button type="button">{action}</button> : null}
      </div>
      {children}
    </section>
  );
}

function FilterRow({ label, count, checked = false, icon }) {
  return (
    <label className={`ride-filter-row ${checked ? "is-checked" : ""}`}>
      <span className="ride-filter-row-left">
        {icon === "radio" ? <RadioIcon checked={checked} /> : <CheckboxIcon checked={checked} />}
        <span>{label}</span>
      </span>
      <span className="ride-filter-count">{count}</span>
    </label>
  );
}

function RideMapPreview() {
  const center = { lat: 4.05, lng: 10.9 };
  const yaounde = { lat: 3.848, lng: 11.502 };
  const douala = { lat: 4.051, lng: 9.767 };

  return (
    <div className="ride-map-card">
      <div className="ride-map-stage">
        <GoogleMapLoader>
          <Map
            defaultZoom={7}
            defaultCenter={center}
            gestureHandling="greedy"
            disableDefaultUI
            style={{ width: "100%", height: "100%" }}
          >
            <AdvancedMarker position={yaounde} title="Yaoundé">
              <Pin background="#0b7a45" borderColor="#0b7a45" glyphColor="#ffffff">
                <span className="ride-map-pin-label">Y</span>
              </Pin>
            </AdvancedMarker>

            <AdvancedMarker position={douala} title="Douala">
              <Pin background="#ef4444" borderColor="#ef4444" glyphColor="#ffffff">
                <span className="ride-map-pin-label">D</span>
              </Pin>
            </AdvancedMarker>
          </Map>
        </GoogleMapLoader>

        <div className="ride-map-overlay">
          <a href="/" className="ride-map-button">
            <MapIcon />
            <span>Afficher sur la carte</span>
          </a>
        </div>
      </div>
    </div>
  );
}

function Sidebar() {
  return (
    <aside className="ride-sidebar">
      <RideMapPreview />

      <div className="ride-filters">
        <SidebarFilterGroup title="Trier par" action="Tout effacer">
          <div className="ride-filter-list">
            {sortOptions.map((option) => (
              <div className={`ride-filter-row ${option.checked ? "is-checked" : ""}`} key={option.label}>
                <span className="ride-filter-row-left">
                  <RadioIcon checked={option.checked} />
                  <span>{option.label}</span>
                </span>
                <span className="ride-filter-count ride-filter-icon">
                  {option.icon === "clock" ? <ClockIcon /> : null}
                  {option.icon === "price" ? <span className="ride-mini-dot" /> : null}
                  {option.icon === "star" ? <StarIcon /> : null}
                  {option.icon === "thumb" ? <ThumbIcon /> : null}
                </span>
              </div>
            ))}
          </div>
        </SidebarFilterGroup>

        <SidebarFilterGroup title="Heure de départ">
          <div className="ride-filter-list">
            {departureSlots.map((option) => (
              <FilterRow key={option.label} {...option} icon="checkbox" />
            ))}
          </div>
        </SidebarFilterGroup>

        <SidebarFilterGroup title="Confort">
          <div className="ride-filter-list">
            {comfortOptions.map((option) => (
              <FilterRow key={option.label} {...option} icon="checkbox" />
            ))}
          </div>
        </SidebarFilterGroup>

        <SidebarFilterGroup title="Prix">
          <div className="ride-range">
            <div className="ride-range-track">
              <span className="ride-range-fill" />
              <span className="ride-range-handle ride-range-handle-left" />
              <span className="ride-range-handle ride-range-handle-right" />
            </div>
            <div className="ride-range-labels">
              <span>2 000 FCFA</span>
              <span>25 000 FCFA</span>
            </div>
          </div>
        </SidebarFilterGroup>

        <SidebarFilterGroup title="Services à bord">
          <div className="ride-filter-list">
            {onboardOptions.map((option) => (
              <FilterRow key={option.label} {...option} icon="checkbox" />
            ))}
          </div>
        </SidebarFilterGroup>

        <SidebarFilterGroup title="Type de véhicule">
          <div className="ride-filter-list">
            {vehicleOptions.map((option) => (
              <FilterRow key={option.label} {...option} icon="checkbox" />
            ))}
          </div>
        </SidebarFilterGroup>
      </div>
    </aside>
  );
}

function ResultTag({ tag, tone }) {
  if (!tag) return null;
  return <span className={`ride-price-tag is-${tone}`}>{tag}</span>;
}

function ResultAmenity({ kind, label }) {
  return (
    <span className="ride-amenity">
      <AmenityIcon kind={kind} />
      <span>{label}</span>
    </span>
  );
}

function ResultCard({ item, onDetails }) {
  return (
    <article className="ride-card">
      <div className="ride-card-main">
        <div className="ride-timeline">
          <div className="ride-time-block">
            <strong>{item.time}</strong>
            <span>{item.from}</span>
            <small>{item.fromSub}</small>
          </div>

          <div className="ride-time-line">
            <span className="ride-time-duration">{item.duration}</span>
            <span className="ride-time-track">
              <i />
            </span>
          </div>

          <div className="ride-time-block is-arrival">
            <strong>{item.arrival}</strong>
            <span>{item.to}</span>
            <small>{item.toSub}</small>
          </div>
        </div>

        <div className="ride-meta-row">
          <span className="ride-mode">
            <ModeIcon mode={item.mode} />
            <span>{item.mode}</span>
          </span>
          <span className="ride-company">
            <span className="ride-avatar">{item.avatar}</span>
            <span>{item.company}</span>
          </span>
          {item.rating ? (
            <span className="ride-rating">
              <StarIcon />
              <span>{item.rating}</span>
            </span>
          ) : null}
          <span className="ride-seats">
            <SeatIcon />
            <span>{item.seats}</span>
          </span>
          <div className="ride-extra-list">
            {item.extras.includes("WiFi") ? <ResultAmenity kind="WiFi" label="Wi‑Fi" /> : null}
            {item.extras.includes("Toilet") ? <ResultAmenity kind="Toilet" label="Toilettes à bord" /> : null}
            {item.extras.includes("Air") ? <ResultAmenity kind="Air" label="Climatisé" /> : null}
          </div>
        </div>
      </div>

      <div className="ride-card-side">
        <div className="ride-price">
          <strong>{item.price}</strong>
          <span>{item.currency}</span>
        </div>
        <ResultTag tag={item.tag} tone={item.tagTone} />
        <button type="button" className="ride-details-btn" onClick={onDetails}>
          Voir les détails
        </button>
      </div>
    </article>
  );
}

function MainResults({ items, searchParams, hasLiveResults, onDetails }) {
  const title = searchParams?.date
    ? formatDateLabel(searchParams.date)
    : "Aujourd'hui, 24 mai";
  const stats = items.reduce((acc, item) => {
    const mode = (item.mode || "").toLowerCase();
    acc.all += 1;
    if (mode.includes("covoiturage") || mode.includes("car")) acc.car += 1;
    if (mode.includes("bus")) acc.bus += 1;
    if (mode.includes("train")) acc.train += 1;
    return acc;
  }, { all: 0, car: 0, bus: 0, train: 0 });

  return (
    <section className="ride-main">
      <div className="ride-tabs">
        {summaryTabs.map((tab) => (
          <div key={tab.label} className={`ride-tab ${tab.active ? "is-active" : ""}`}>
            <div className="ride-tab-top">
              <span className="ride-tab-icon">
                {tab.mode === "car" ? <CarIcon /> : null}
                {tab.mode === "bus" ? <BusIcon /> : null}
                {tab.mode === "train" ? <TrainIcon /> : null}
                {tab.mode === "all" ? <CarIcon /> : null}
              </span>
              <span>{tab.label}</span>
              <strong>
                {tab.mode === "all"
                  ? stats.all
                  : tab.mode === "car"
                    ? stats.car
                    : tab.mode === "bus"
                      ? stats.bus
                      : stats.train}
              </strong>
            </div>
            {tab.subtitle ? <small>{tab.subtitle}</small> : null}
          </div>
        ))}
      </div>

      <div className="ride-list-head">
        <h2>{title}</h2>
        <strong>{hasLiveResults ? `${items.length} trajets disponibles` : "72 trajets disponibles"}</strong>
      </div>

      <div className="ride-cards">
        {items.length ? (
          items.map((item) => (
            <ResultCard
              key={item.id || `${item.time}-${item.mode}-${item.price}`}
              item={item}
              onDetails={() => onDetails(item)}
            />
          ))
        ) : hasLiveResults ? (
          <div className="ride-empty-state">
            <h3>Aucun trajet disponible</h3>
            <p>Essayez une autre date, un autre départ ou une autre destination.</p>
          </div>
        ) : (
          items.map((item) => (
            <ResultCard key={`${item.time}-${item.mode}-${item.price}`} item={item} />
          ))
        )}
      </div>

      <div className="ride-load-more">
        <button type="button">Charger plus de résultats</button>
      </div>
    </section>
  );
}

export default function HomeRide() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState(() => location.state?.searchParams || DEFAULT_SEARCH_PARAMS);
  const [searchResults, setSearchResults] = useState(() => (
    Array.isArray(location.state?.searchResults) ? location.state.searchResults : []
  ));
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(() => Boolean(location.state && Array.isArray(location.state.searchResults)));
  const items = isSearchActive ? searchResults : resultCards;

  useEffect(() => {
    setSearchParams(location.state?.searchParams || DEFAULT_SEARCH_PARAMS);
    setSearchResults(Array.isArray(location.state?.searchResults) ? location.state.searchResults : []);
    setIsSearchActive(Boolean(location.state && Array.isArray(location.state.searchResults)));
  }, [location.key, location.state]);

  const runSearch = async () => {
    const from = (searchParams.from || "").trim();
    const to = (searchParams.to || "").trim();
    if (!from || !to) return;

    setSearchLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/api/v1/search`, {
        params: {
          from,
          to,
          date: formatDateForApi(searchParams.date || DEFAULT_SEARCH_PARAMS.date),
          passengers: Number(searchParams.passengers || 1),
          modes: "carpool,train",
          max_price: 5000,
          sort_by: "departure_at",
          page: 0,
          limit: 20,
        },
      });
      const results = response?.data?.results || [];
      setSearchResults(results.map(normalizeSearchResult));
      setIsSearchActive(true);
    } catch (error) {
      setSearchResults([]);
      setIsSearchActive(true);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleOpenDetails = (item) => {
    navigate("/home-ride-details", {
      state: {
        selectedRide: item.raw || item,
        searchParams,
        searchResults,
      },
    });
  };

  return (
    <div className="bbc-page ride-page">
      <Header />
      <main className="ride-main-shell">
        <div className="bbc-container ride-page-inner">
          <SearchBar
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            onSearch={runSearch}
            searchLoading={searchLoading}
          />
          <div className="ride-results-layout">
            <Sidebar />
            <MainResults
              items={items}
              searchParams={searchParams}
              hasLiveResults={isSearchActive}
              onDetails={handleOpenDetails}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
